import { cn } from '@dialogueconsulting/sked-ui/lib/utils'
import type { PropsWithChildren } from 'react'

import {
  CarouselIcon,
  ReelIcon,
  VideoIcon,
  StoryIcon,
  TextIcon,
  UnknownPlatformLogo,
  PlaceholderPhone,
  RejectIcon,
} from 'Icons'
import { type PostType } from 'Types'

export interface PostPreviewGridProps {
  account: {
    name: string
    handler_name: string
    avatar: string
  }
  posts: {
    id: string
    status: string | 'posted'
    media: {
      type: PostType
      src: string
    }
  }[]
  isHighlightFirstGridItem?: boolean
  isReadOnly: boolean
  scrollableGrid?: boolean
}

// Status corner-markers — exported because the mobile grid (MobilePreviewGrid)
// reuses them. Colours follow the token palette (yellow-500 / green-500 /
// red-500), consistent with the calendar status icons.
export const MediaIconWrap = ({ children }: PropsWithChildren) => (
  <div className="absolute right-0 top-0 w-[25px] p-[5px]">{children}</div>
)

export const IsPendingMarker = () => (
  <UnknownPlatformLogo className="absolute bottom-0 right-0 z-[2] w-[25px] rounded-none rounded-tl-[5px] [&_path]:fill-neutral-800 [&_rect]:fill-yellow-500" />
)

export const IsApprovedMarker = ({ children }: PropsWithChildren) => (
  <div className="absolute bottom-0 right-0 z-[2] grid size-[25px] place-content-center rounded-tl-[5px] bg-green-500 text-[12px] text-white [&_svg]:w-[14px] [&_svg_path]:fill-white">
    {children}
  </div>
)

const IsRejectedMarker = ({ children }: PropsWithChildren) => (
  <div className="absolute bottom-0 right-0 z-[2] grid size-[25px] place-content-center rounded-tl-[5px] bg-red-500 text-white [&_svg]:w-[21px] [&_svg_path]:fill-white [&_svg_rect]:fill-transparent">
    {children}
  </div>
)

export const renderPostTypeIcon = (type: PostType) => {
  switch (type) {
    case 'carousel':
      return <CarouselIcon />
    case 'singleImage':
      return null
    case 'video':
      return <VideoIcon />
    case 'reel':
      return <ReelIcon />
    case 'story':
      return <StoryIcon />
    case 'text':
      return <TextIcon data-testid="text_icon" />
  }
}

const PostPreviewGrid: React.FC<
  PostPreviewGridProps & React.HTMLAttributes<HTMLDivElement>
> = ({
  account,
  posts,
  isHighlightFirstGridItem,
  className,
  isReadOnly,
  scrollableGrid,
}) => {
  return (
    <div
      className={cn('relative max-md:ml-[53px] max-md:mt-[22px]', className)}
    >
      <PlaceholderPhone className="phone-bg absolute top-0 w-[400px] min-w-[400px]" />
      <div className="container" data-testid="preview-grid-container">
        <div className="flex items-center justify-center text-xs font-bold">
          <img
            src={account.avatar}
            alt={account.handler_name}
            className="mr-2 size-[38px] rounded-full"
          />
          <div>
            <p className="m-0">{account.name}</p>
            <p className="m-0 font-normal leading-[10px]">
              {account.handler_name}
            </p>
          </div>
        </div>
        <div
          className={cn(
            'grid-container mx-auto mt-4 grid max-h-[387px] w-[295px] grid-cols-3 gap-[5px]',
            scrollableGrid ? 'overflow-auto' : 'overflow-hidden'
          )}
        >
          {posts.map((post, index) => {
            const isFirst = index === 0
            return (
              <div
                key={post.id}
                className={cn(
                  'relative aspect-[4/5]',
                  '[&>img]:absolute [&>img]:inset-0 [&>img]:size-full [&>img]:object-cover',
                  '[&:first-child]:rounded-tl-[10px] [&:first-child>img]:rounded-tl-[6px]',
                  '[&:nth-child(3)]:rounded-tr-[10px] [&:nth-child(3)>img]:rounded-tr-[10px]',
                  '[&:nth-child(7)]:rounded-bl-[10px] [&:nth-child(7)>img]:rounded-bl-[10px]',
                  '[&:nth-child(10)]:rounded-bl-[10px] [&:nth-child(10)>img]:rounded-bl-[10px]',
                  isFirst && 'first-grid-item',
                  isFirst &&
                    isHighlightFirstGridItem &&
                    'border-4 border-blue-500',
                  post.status === 'posted' && 'opacity-70'
                )}
              >
                <img src={post.media.src} alt={post.media.type} />
                <MediaIconWrap>
                  {renderPostTypeIcon(post.media.type)}
                </MediaIconWrap>
                {!isReadOnly && (
                  <>
                    {post.status === 'pending' && <IsPendingMarker />}
                    {post.status === 'approved' && (
                      <IsApprovedMarker>
                        <i className="fa-solid fa-check" aria-hidden="true" />
                      </IsApprovedMarker>
                    )}
                    {post.status === 'requestChange' && (
                      <IsRejectedMarker>
                        <RejectIcon />
                      </IsRejectedMarker>
                    )}
                  </>
                )}
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

export default PostPreviewGrid
